import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import MUIDataTable from "mui-datatables";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAttendance,
  fetchEmployees,
  getWfhUsage,
  markWfh,
  unmarkWfh,
} from "../../Redux/actions";

const WorkFromHome = () => {
  const dispatch = useDispatch();
  const { payload: attendancePayload } = useSelector((state) => state.attendance);
  const employees = useSelector((state) => state.employees.payload);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [employeeId, setEmployeeId] = useState("");
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  useEffect(() => {
    if (!employees || employees.length === 0) {
      dispatch(fetchEmployees());
    }
  }, [dispatch, employees]);

  const isAllEmployees = employeeId === "all";
  const isSingleEmployee = employeeId && !isAllEmployees;

  const loadAttendanceAndUsage = () => {
    if (!employeeId) return;
    dispatch(
      fetchAttendance({
        month,
        employee: isAllEmployees ? null : employeeId,
      })
    );
    if (isSingleEmployee) {
      setLoadingUsage(true);
      dispatch(getWfhUsage(employeeId, month)).then((res) => {
        setLoadingUsage(false);
        if (res?.data?.success && res?.data?.payload) {
          setUsage(res.data.payload);
        } else {
          setUsage(null);
        }
      });
    } else {
      setUsage(null);
    }
  };

  useEffect(() => {
    if (employeeId) {
      loadAttendanceAndUsage();
    } else {
      setUsage(null);
    }
  }, [employeeId, month]);

  const getRowEmployeeId = (row) => {
    const e = row.employee_id || row.employee;
    return e && (typeof e === "object" ? e._id : e);
  };

  // Count how many WFH days this employee already has (for disabling when limit is 2)
  const getWfhCountForEmployee = (empId) => {
    if (!empId || !attendancePayload.length) return 0;
    return attendancePayload.filter(
      (r) => getRowEmployeeId(r) === empId && !!r.work_from_home
    ).length;
  };

  const handleMarkWfh = (row) => {
    const dateStr = moment(row.date, "DD/MM/YYYY").format("YYYY-MM-DD");
    const empId = isAllEmployees ? getRowEmployeeId(row) : employeeId;
    if (!empId) return;
    dispatch(markWfh(empId, dateStr)).then((res) => {
      if (res?.data?.success) {
        loadAttendanceAndUsage();
      }
    });
  };

  const handleUnmarkWfh = (row) => {
    const dateStr = moment(row.date, "DD/MM/YYYY").format("YYYY-MM-DD");
    const empId = isAllEmployees ? getRowEmployeeId(row) : employeeId;
    if (!empId) return;
    dispatch(unmarkWfh(empId, dateStr)).then((res) => {
      if (res?.data?.success) {
        loadAttendanceAndUsage();
      }
    });
  };

  const columns = [
    {
      name: "_id",
      label: "ID",
      options: { filter: true, sort: true, display: false },
    },
    ...(isAllEmployees
      ? [
          {
            name: "name",
            label: "Employee",
            options: { filter: true, sort: true },
          },
        ]
      : []),
    {
      name: "date",
      label: "Date",
      options: { filter: true, sort: true },
    },
    {
      name: "week",
      label: "Day",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const row = attendancePayload[tableMeta.rowIndex];
          if (!row?.date) return value || "-";
          const dayName = moment(row.date, "DD/MM/YYYY").format("ddd");
          return dayName;
        },
      },
    },
    {
      name: "clock_In",
      label: "Clock In",
      options: { filter: true, sort: true },
    },
    {
      name: "clock_Out",
      label: "Clock Out",
      options: { filter: true, sort: true },
    },
    {
      name: "work_from_home",
      label: "WFH",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value ? "Yes" : "No"),
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const row = attendancePayload[tableMeta.rowIndex];
          if (!row) return null;
          const isWfh = !!row.work_from_home;
          return isWfh ? (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleUnmarkWfh(row)}
            >
              Unmark WFH
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleMarkWfh(row)}
              disabled={
                isSingleEmployee
                  ? usage !== null &&
                    usage.remaining !== undefined &&
                    usage.remaining <= 0
                  : getWfhCountForEmployee(getRowEmployeeId(row)) >= 2
              }
            >
              Mark WFH
            </Button>
          );
        },
      },
    },
  ];

  const tableData = attendancePayload.map((row) => {
    const name =
      row.name ||
      (row.employee_id &&
        typeof row.employee_id === "object" &&
        `${row.employee_id.first_name || ""} ${row.employee_id.last_name || ""}`.trim()) ||
      (row.employee &&
        `${row.employee.first_name || ""} ${row.employee.last_name || ""}`.trim()) ||
      "-";
    return {
      _id: row._id,
      name,
      date: row.date,
      week: moment(row.date, "DD/MM/YYYY").format("ddd"),
      clock_In: row.clock_In ?? "-",
      clock_Out: row.clock_Out ?? "-",
      work_from_home: row.work_from_home ?? false,
      employee_id: row.employee_id,
      employee: row.employee,
    };
  });

  return (
    <Card className="mui-data-table" style={{ padding: "1rem" }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          loadAttendanceAndUsage();
        }}
        className="d-flex flex-wrap align-items-center justify-content-between mb-3"
      >
        <div className="d-flex flex-wrap align-items-center gap-3">
          <FormControl style={{ minWidth: 200 }}>
            <InputLabel id="wfh-employee" shrink>
              Employee
            </InputLabel>
            <Select
              labelId="wfh-employee"
              displayEmpty
              value={employeeId || ""}
              onChange={(e) => setEmployeeId(e.target.value || "")}
              required
            >
              <MenuItem value="">Select employee</MenuItem>
              <MenuItem value="all">All Employees</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp._id} value={emp._id}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <input
              type="month"
              id="wfh-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="form-control"
              style={{ padding: "8px 12px", minWidth: 160 }}
            />
          </FormControl>
          <Button type="submit" variant="contained" color="primary">
            Load
          </Button>
        </div>
      </form>

      {employeeId && (
        <>
          <div className="mb-3 p-3 bg-light rounded">
            <strong>Work from home (this month)</strong>
            <span className="ml-2">
              Max 2 per month; not carried forward.
            </span>
            {isSingleEmployee && (loadingUsage ? (
              <div className="mt-2">Loading usage…</div>
            ) : usage !== null ? (
              <div className="mt-2">
                Used: <strong>{usage.used}</strong> / Limit:{" "}
                <strong>{usage.limit}</strong> — Remaining:{" "}
                <strong>{usage.remaining}</strong>
              </div>
            ) : null)}
            {isAllEmployees && (
              <div className="mt-2 text-muted">
                WFH usage (used/remaining) is per employee. Select an employee to see their usage.
              </div>
            )}
          </div>

          <MUIDataTable
            title="Attendance — Mark / Unmark Work from Home"
            data={tableData}
            columns={columns}
            options={{
              selectableRows: "none",
              filter: false,
              print: false,
              download: false,
              responsive: "standard",
            }}
          />
        </>
      )}

      {!employeeId && (
        <p className="text-muted">Select an employee and month to view and manage work from home.</p>
      )}
    </Card>
  );
};

export default WorkFromHome;
